import { ContinuationDiscountApplyLimit, Discount, DiscountContinuation } from '../../types/DiscountTypes'
import { getContinuationApplyLimit, getContinuationQualifyingPeriodIds } from '../DiscountHelper'
import { fixMathRounding, getPriceFromCalculation, getBasePriceFromCalculation } from './CalculateDiscountHelpers'
import {
  CalculateDiscountLogic,
  Calculation,
  CalculationDiscount,
  CalculationUserInput,
  DiscountType
} from './CalculateDiscountTypes'

export class ContinuationDiscountLogic implements CalculateDiscountLogic {
  type: DiscountType = 'continuation'
  discountDefinition: Discount | DiscountContinuation

  constructor(discount: Discount) {
    this.discountDefinition = discount
  }

  chooseEligible(previousCalculations: Calculation[], userInput: CalculationUserInput): string[] {
    const def = this.discountDefinition as DiscountContinuation
    const userEnrolledPeriodIds = userInput.userEnrolledPeriodIds || []
    const userEnrolledActivityIds = userInput.userEnrolledActivityIds || []
    const usedContinuationDiscountIds = userInput.usedContinuationDiscountIds || []

    const specificActivities = def.qualifyingActivityIds || []

    if (specificActivities.length > 0) {
      // When qualifying activities are explicitly listed, enrollment in one is sufficient —
      // the period check is skipped. This handles import-course users whose stored activities
      // carry no periodId but are still intended to qualify for the continuation discount.
      if (!specificActivities.some((id) => userEnrolledActivityIds.includes(id))) return []
    } else {
      // No specific activities configured: user must be enrolled in at least one qualifying period.
      const qualifyingPeriodIds = getContinuationQualifyingPeriodIds(def)
      if (!qualifyingPeriodIds.some((id) => userEnrolledPeriodIds.includes(id))) return []
    }

    const assignedActivityIds = previousCalculations
      .filter((calc) => calc.activity.discountIds.includes(def.id))
      .map((calc) => calc.activity.id)

    if (getContinuationApplyLimit(def) === ContinuationDiscountApplyLimit.Activity) {
      const usedOnActivityIds = new Set(
        (userInput.usedContinuationDiscountActivities || [])
          .filter((used) => used.discountId === def.id)
          .map((used) => used.activityId)
      )
      return assignedActivityIds.filter((activityId) => !usedOnActivityIds.has(activityId))
    }

    if (usedContinuationDiscountIds.includes(def.id)) return []
    return assignedActivityIds
  }

  calculate(previousCalculations: Calculation[], userInput: CalculationUserInput): Calculation[] {
    const eligibleActivities = this.chooseEligible(previousCalculations, userInput)

    previousCalculations.forEach((calculation) => {
      if (!eligibleActivities.includes(calculation.activity.id)) return

      const activityPrice = getPriceFromCalculation(calculation)
      const basePrice = getBasePriceFromCalculation(calculation)
      const discountAmount =
        this.discountDefinition.calculationType === 'amount'
          ? this.discountDefinition.value
          : fixMathRounding(basePrice * (this.discountDefinition.value / 100))

      const continuationDiscount: CalculationDiscount = {
        type: this.type,
        name: this.discountDefinition.name,
        id: this.discountDefinition.id,
        input: activityPrice,
        discount: discountAmount,
        output: fixMathRounding(activityPrice - discountAmount),
        discountCalculationType: this.discountDefinition.calculationType,
        discountValue: this.discountDefinition.value,
        discountGroup: this.discountDefinition.discountGroup
      }

      calculation.discounts = calculation.discounts || []
      calculation.discounts.push(continuationDiscount)
      calculation.finalPrice = continuationDiscount.output
    })

    return previousCalculations
  }
}
